import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router";
import type {
  TutorialDemoOverlay,
  TutorialSection,
  TutorialSectionId,
  TutorialStep,
  TourTarget,
} from "../../Constants/tutorialSteps";
import {
  buildTutorialStepsForSection,
  getRoleTutorialLabel,
  getTutorialSectionsForSteps,
} from "../../Constants/tutorialSteps";
import { USER_ROLE } from "../../Types/user";
import {
  measureTourTargetElements,
  scrollTourTargetIntoView,
  waitForTourPanel,
  waitForTourTarget,
  type TourRect,
} from "../../utils/tutorialTargeting";
import {
  computeTooltipStyle,
  fallbackTooltipStyle,
} from "../../utils/tutorialTooltipPosition";
import { getTourSidebarWidth } from "../../utils/tutorialViewport";
import {
  getTutorialAutoPlayDurationMs,
  formatTutorialDurationEstimate,
  getTutorialSectionAutoPlayDurationMs,
  readTutorialAutoPlayPreference,
  writeTutorialAutoPlayPreference,
} from "../../utils/tutorialAutoPlay";
import {
  cancelTutorialSpeech,
  estimateSpeechDurationMs,
  formatTutorialSpeechRateLabel,
  isTutorialSpeechSupported,
  readTutorialSpeechRatePreference,
  readTutorialVoiceMutedPreference,
  speakTutorialNarration,
  TUTORIAL_SPEECH_END_BEAT_MS,
  TUTORIAL_SPEECH_RATES,
  type TutorialSpeechRate,
  writeTutorialSpeechRatePreference,
  writeTutorialVoiceMutedPreference,
} from "../../utils/tutorialSpeech";
import { getTutorialNarration } from "../../Constants/tutorialNarration";
import { TutorialDemoOverlays } from "./TutorialDemoOverlays";
import "../../Css/Tutorial/TutorialModal.css";

type InteractiveTutorialProps = {
  steps: TutorialStep[];
  userRole: USER_ROLE;
  onComplete: () => void;
  onStepChange?: (step: TutorialStep, stepIndex: number) => void;
};

type TooltipStyle = ReturnType<typeof computeTooltipStyle>;

function isSocialDemoOverlay(overlay: TutorialDemoOverlay | undefined): boolean {
  return overlay === "social-prompt" || overlay === "social-prompt-request";
}

function demoOverlayFamily(
  overlay: TutorialDemoOverlay | undefined,
): "social" | "insight-modal" | null {
  if (isSocialDemoOverlay(overlay)) return "social";
  if (overlay === "complete-insight" || overlay === "dismiss-insight") {
    return "insight-modal";
  }
  return null;
}

function isDemoOverlayMounted(overlay: TutorialDemoOverlay | undefined): boolean {
  if (isSocialDemoOverlay(overlay)) {
    return Boolean(document.querySelector('[data-tour="social-prompt-modal"]'));
  }
  if (overlay === "complete-insight" || overlay === "dismiss-insight") {
    return Boolean(
      document.querySelector(
        '[data-tour="insight-complete-modal"], [data-tour="insight-dismiss-modal"]',
      ),
    );
  }
  return false;
}

function normalizeTourSearch(search: string): string {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  if (!raw) return "";
  const params = new URLSearchParams(raw);
  const normalized = new URLSearchParams();
  for (const key of [...params.keys()].sort()) {
    for (const value of params.getAll(key)) {
      normalized.append(key, value);
    }
  }
  return `?${normalized.toString()}`;
}

function routesMatch(stepRoute: string | undefined, pathname: string, search: string): boolean {
  if (!stepRoute) return true;
  const qIndex = stepRoute.indexOf("?");
  const expectedPath = qIndex >= 0 ? stepRoute.slice(0, qIndex) : stepRoute;
  const expectedSearch = qIndex >= 0 ? stepRoute.slice(qIndex) : "";
  return (
    pathname === expectedPath &&
    normalizeTourSearch(search) === normalizeTourSearch(expectedSearch)
  );
}

async function expandTourSection(
  sectionId: string | undefined,
  panelTarget?: string,
): Promise<void> {
  if (!sectionId) return;
  const trigger = document.getElementById(sectionId);
  if (trigger?.getAttribute("aria-expanded") !== "false") return;
  trigger.click();
  if (panelTarget) {
    await waitForTourPanel(panelTarget as TourTarget);
    return;
  }
  await new Promise((resolve) => window.setTimeout(resolve, 320));
}

function TutorialAutoPlayToggle({
  enabled,
  onToggle,
  compact = false,
}: {
  enabled: boolean;
  onToggle: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      className={`tutorialAutoPlay_toggle${compact ? " tutorialAutoPlay_toggle--compact" : ""}${
        enabled ? " tutorialAutoPlay_toggle--on" : ""
      }`}
      onClick={onToggle}
      aria-pressed={enabled}
      aria-label={enabled ? "Pause auto-play" : "Enable auto-play"}
      title={enabled ? "Pause auto-play" : "Enable auto-play"}
    >
      <i className={`fa-solid ${enabled ? "fa-pause" : "fa-play"}`} aria-hidden />
      {!compact ? (
        <span>{enabled ? "Auto-play on" : "Auto-play"}</span>
      ) : null}
    </button>
  );
}

function TutorialVoiceMuteToggle({
  muted,
  onToggle,
  compact = false,
  disabled = false,
}: {
  muted: boolean;
  onToggle: () => void;
  compact?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`tutorialVoice_toggle${compact ? " tutorialVoice_toggle--compact" : ""}${
        muted ? "" : " tutorialVoice_toggle--on"
      }`}
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={!muted}
      aria-label={muted ? "Unmute narration" : "Mute narration"}
      title={
        disabled
          ? "Voice narration isn’t available in this browser"
          : muted
            ? "Unmute narration"
            : "Mute narration"
      }
    >
      <i className={`fa-solid ${muted ? "fa-volume-xmark" : "fa-volume-high"}`} aria-hidden />
      {!compact ? <span>{muted ? "Muted" : "Voice"}</span> : null}
    </button>
  );
}

function TutorialSpeechSpeedControl({
  rate,
  onChange,
  disabled = false,
}: {
  rate: TutorialSpeechRate;
  onChange: (rate: TutorialSpeechRate) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopImmediatePropagation();
      setOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  return (
    <div className="tutorialSpeechSpeed" ref={rootRef}>
      <button
        type="button"
        className={`tutorialSpeechSpeed_toggle${open ? " tutorialSpeechSpeed_toggle--open" : ""}`}
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Narration speed ${formatTutorialSpeechRateLabel(rate)}`}
        title={
          disabled
            ? "Voice narration isn’t available in this browser"
            : `Narration speed ${formatTutorialSpeechRateLabel(rate)}`
        }
      >
        {formatTutorialSpeechRateLabel(rate)}
      </button>
      {open ? (
        <div className="tutorialSpeechSpeed_menu" role="menu" aria-label="Narration speed">
          {TUTORIAL_SPEECH_RATES.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={option === rate}
              className={`tutorialSpeechSpeed_option${
                option === rate ? " tutorialSpeechSpeed_option--active" : ""
              }`}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              {formatTutorialSpeechRateLabel(option)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TutorialAutoPlayProgress({
  progress,
  visible,
}: {
  progress: number;
  visible: boolean;
}) {
  if (!visible) return null;
  return (
    <div
      className="tutorialAutoPlay_track"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      aria-label="Auto-play progress"
    >
      <div
        className="tutorialAutoPlay_fill"
        style={{ transform: `scaleX(${Math.min(1, Math.max(0, progress))})` }}
      />
    </div>
  );
}

function TutorialSectionPicker({
  welcomeStep,
  roleLabel,
  sections,
  roleSteps,
  autoPlay,
  onAutoPlayChange,
  voiceMuted,
  onVoiceMutedChange,
  speechRate,
  onSpeechRateChange,
  speechSupported,
  onSelect,
  onClose,
  onSkip,
}: {
  welcomeStep: TutorialStep;
  roleLabel: string;
  sections: TutorialSection[];
  roleSteps: TutorialStep[];
  autoPlay: boolean;
  onAutoPlayChange: (enabled: boolean) => void;
  voiceMuted: boolean;
  onVoiceMutedChange: (muted: boolean) => void;
  speechRate: TutorialSpeechRate;
  onSpeechRateChange: (rate: TutorialSpeechRate) => void;
  speechSupported: boolean;
  onSelect: (sectionId: TutorialSectionId) => void;
  onClose: () => void;
  onSkip: () => void;
}) {
  const fullSection = sections.find((section) => section.id === "full");
  const topicSections = sections.filter((section) => section.id !== "full");
  const voiceEnabled = speechSupported && !voiceMuted;

  const sectionDurationLabel = (sectionId: TutorialSectionId) => {
    const pathSteps = buildTutorialStepsForSection(roleSteps, sectionId);
    const ms = getTutorialSectionAutoPlayDurationMs(pathSteps, {
      voiceEnabled,
      speechRate,
    });
    return formatTutorialDurationEstimate(ms);
  };

  return (
    <div
      className="tutorialModal_shell glassModal_shell tutorialModal_shell--center tutorialModal_shell--picker"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorialModal_title"
      style={
        {
          "--tutorial-accent": welcomeStep.accent,
          "--tutorial-accent-soft": welcomeStep.accentSoft,
        } as CSSProperties
      }
      onClick={(event) => event.stopPropagation()}
    >
      <div className="tutorialModal_topBar">
        <div className="tutorialModal_brand">
          <span className="tutorialModal_brandIcon" aria-hidden>
            <i className="fa-solid fa-graduation-cap" />
          </span>
          <div>
            <p className="tutorialModal_brandEyebrow">App guide</p>
            <p className="tutorialModal_brandRole">{roleLabel} tour</p>
          </div>
        </div>
        <div className="tutorialModal_topBarActions">
          <TutorialSpeechSpeedControl
            rate={speechRate}
            onChange={onSpeechRateChange}
            disabled={!speechSupported}
          />
          <TutorialVoiceMuteToggle
            muted={voiceMuted || !speechSupported}
            onToggle={() => onVoiceMutedChange(!voiceMuted)}
            disabled={!speechSupported}
          />
          <TutorialAutoPlayToggle
            enabled={autoPlay}
            onToggle={() => onAutoPlayChange(!autoPlay)}
          />
          <button type="button" className="tutorialModal_close" onClick={onClose} aria-label="Close guide">
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      </div>

      <div className="tutorialModal_body">
        <div className="tutorialModal_hero">
          <div className="tutorialModal_iconWrap" aria-hidden>
            <i className={`fa-solid ${welcomeStep.icon}`} />
          </div>
          <div className="tutorialModal_copy">
            <p className="tutorialModal_kicker">{welcomeStep.kicker}</p>
            <h2 id="tutorialModal_title" className="tutorialModal_title">
              {welcomeStep.title}
            </h2>
            <p className="tutorialModal_subtitle">{welcomeStep.subtitle}</p>
          </div>
        </div>
        <p className="tutorialModal_description">{welcomeStep.body}</p>

        {fullSection ? (
          <button
            type="button"
            className="tutorialModal_sectionCard tutorialModal_sectionCard--full"
            onClick={() => onSelect(fullSection.id)}
          >
            <span className="tutorialModal_sectionIcon" aria-hidden>
              <i className={`fa-solid ${fullSection.icon}`} />
            </span>
            <span className="tutorialModal_sectionCopy">
              <span className="tutorialModal_sectionLabelRow">
                <span className="tutorialModal_sectionLabel">{fullSection.label}</span>
                <span className="tutorialModal_sectionEta">
                  <i className="fa-regular fa-clock" aria-hidden />
                  {sectionDurationLabel(fullSection.id)}
                </span>
              </span>
              <span className="tutorialModal_sectionDesc">{fullSection.description}</span>
            </span>
            <span className="tutorialModal_sectionArrow" aria-hidden>
              <i className="fa-solid fa-arrow-right" />
            </span>
          </button>
        ) : null}

        <p className="tutorialModal_sectionDivider">Or jump to one area</p>

        <div className="tutorialModal_sectionGrid" role="list">
          {topicSections.map((section) => (
            <button
              key={section.id}
              type="button"
              className="tutorialModal_sectionCard"
              role="listitem"
              onClick={() => onSelect(section.id)}
            >
              <span className="tutorialModal_sectionIcon" aria-hidden>
                <i className={`fa-solid ${section.icon}`} />
              </span>
              <span className="tutorialModal_sectionCopy">
                <span className="tutorialModal_sectionLabelRow">
                  <span className="tutorialModal_sectionLabel">{section.label}</span>
                  <span className="tutorialModal_sectionEta">
                    <i className="fa-regular fa-clock" aria-hidden />
                    {sectionDurationLabel(section.id)}
                  </span>
                </span>
                <span className="tutorialModal_sectionDesc">{section.description}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="tutorialModal_footer">
        <button type="button" className="tutorialModal_footerLink" onClick={onSkip}>
          Skip tour
        </button>
      </div>
    </div>
  );
}

function CenterStepCard({
  step,
  roleLabel,
  stepIndex,
  stepCount,
  isFirst,
  isLast,
  autoPlay,
  autoPlayProgress,
  onToggleAutoPlay,
  voiceMuted,
  onToggleVoiceMute,
  speechRate,
  onSpeechRateChange,
  speechSupported,
  onClose,
  onBack,
  onNext,
  onSkip,
  backLabel,
}: {
  step: TutorialStep;
  roleLabel: string;
  stepIndex: number;
  stepCount: number;
  isFirst: boolean;
  isLast: boolean;
  autoPlay: boolean;
  autoPlayProgress: number;
  onToggleAutoPlay: () => void;
  voiceMuted: boolean;
  onToggleVoiceMute: () => void;
  speechRate: TutorialSpeechRate;
  onSpeechRateChange: (rate: TutorialSpeechRate) => void;
  speechSupported: boolean;
  onClose: () => void;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  backLabel?: string;
}) {
  return (
    <div
      className="tutorialModal_shell glassModal_shell tutorialModal_shell--center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorialModal_title"
      style={
        {
          "--tutorial-accent": step.accent,
          "--tutorial-accent-soft": step.accentSoft,
        } as CSSProperties
      }
      onClick={(event) => event.stopPropagation()}
    >
      <TutorialAutoPlayProgress progress={autoPlayProgress} visible={autoPlay} />
      <div className="tutorialModal_topBar">
        <div className="tutorialModal_brand">
          <span className="tutorialModal_brandIcon" aria-hidden>
            <i className="fa-solid fa-graduation-cap" />
          </span>
          <div>
            <p className="tutorialModal_brandEyebrow">App guide</p>
            <p className="tutorialModal_brandRole">{roleLabel} tour</p>
          </div>
        </div>
        <div className="tutorialModal_topBarActions">
          <TutorialSpeechSpeedControl
            rate={speechRate}
            onChange={onSpeechRateChange}
            disabled={!speechSupported}
          />
          <TutorialVoiceMuteToggle
            muted={voiceMuted || !speechSupported}
            onToggle={onToggleVoiceMute}
            disabled={!speechSupported}
          />
          <TutorialAutoPlayToggle enabled={autoPlay} onToggle={onToggleAutoPlay} />
          <button type="button" className="tutorialModal_close" onClick={onClose} aria-label="Close guide">
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      </div>

      <div className="tutorialModal_progressRow">
        <span className="tutorialModal_progressLabel">
          Step {stepIndex + 1} of {stepCount}
        </span>
      </div>

      <div className="tutorialModal_body">
        <div className="tutorialModal_hero">
          <div className="tutorialModal_iconWrap" aria-hidden>
            <i className={`fa-solid ${step.icon}`} />
          </div>
          <div className="tutorialModal_copy">
            <p className="tutorialModal_kicker">{step.kicker}</p>
            <h2 id="tutorialModal_title" className="tutorialModal_title">
              {step.title}
            </h2>
            <p className="tutorialModal_subtitle">{step.subtitle}</p>
          </div>
        </div>
        <p className="tutorialModal_description">{step.body}</p>
        <ul className="tutorialModal_bullets">
          {step.bullets.map((bullet) => (
            <li key={bullet}>
              <span className="tutorialModal_bulletIcon" aria-hidden>
                <i className="fa-solid fa-check" />
              </span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>

      <TutorialFooter
        isFirst={isFirst}
        isLast={isLast}
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        nextLabel={isLast ? "Get started" : "Next"}
        backLabel={backLabel}
      />
    </div>
  );
}

function TutorialFooter({
  isFirst,
  isLast,
  onBack,
  onNext,
  onSkip,
  nextLabel,
  backLabel,
}: {
  isFirst: boolean;
  isLast: boolean;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  nextLabel?: string;
  backLabel?: string;
}) {
  return (
    <div className="tutorialModal_footer">
      <button type="button" className="tutorialModal_footerLink" onClick={onSkip}>
        Skip tour
      </button>
      <div className="tutorialModal_footerActions">
        {!isFirst || backLabel ? (
          <button type="button" className="tutorialModal_button tutorialModal_button--secondary" onClick={onBack}>
            {backLabel ?? "Back"}
          </button>
        ) : null}
        <button type="button" className="tutorialModal_button tutorialModal_button--primary" onClick={onNext}>
          {nextLabel ?? (isLast ? "Finish" : "Next")}
        </button>
      </div>
    </div>
  );
}

export function InteractiveTutorial({
  steps,
  userRole,
  onComplete,
  onStepChange,
}: InteractiveTutorialProps) {
  const [activeSteps, setActiveSteps] = useState<TutorialStep[] | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<TourRect | null>(null);
  const [targetReady, setTargetReady] = useState(false);
  const [targetMissing, setTargetMissing] = useState(false);
  const [autoPlay, setAutoPlay] = useState(() => readTutorialAutoPlayPreference());
  const [autoPlayProgress, setAutoPlayProgress] = useState(0);
  const [voiceMuted, setVoiceMuted] = useState(() => {
    if (!isTutorialSpeechSupported()) return true;
    return readTutorialVoiceMutedPreference();
  });
  const [speechRate, setSpeechRate] = useState<TutorialSpeechRate>(() =>
    readTutorialSpeechRatePreference(),
  );
  const [speechComplete, setSpeechComplete] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const goNextRef = useRef<() => void>(() => {});
  const speechSupported = isTutorialSpeechSupported();
  const voiceEnabled = speechSupported && !voiceMuted;

  const welcomeStep = steps.find((step) => step.id === "welcome") ?? steps[0];
  const sections = getTutorialSectionsForSteps(steps);
  const tourSteps = activeSteps ?? [];
  const step = activeSteps ? tourSteps[stepIndex] : welcomeStep;
  const isFirst = stepIndex === 0;
  const isLast = activeSteps != null && stepIndex === tourSteps.length - 1;
  const roleLabel = getRoleTutorialLabel(userRole);
  const showingPicker = activeSteps == null;

  const setAutoPlayPreference = useCallback((enabled: boolean) => {
    setAutoPlay(enabled);
    writeTutorialAutoPlayPreference(enabled);
    if (!enabled) setAutoPlayProgress(0);
  }, []);

  const toggleAutoPlay = useCallback(() => {
    setAutoPlayPreference(!autoPlay);
  }, [autoPlay, setAutoPlayPreference]);

  const setVoiceMutedPreference = useCallback((muted: boolean) => {
    setVoiceMuted(muted);
    writeTutorialVoiceMutedPreference(muted);
    if (muted) cancelTutorialSpeech();
  }, []);

  const toggleVoiceMute = useCallback(() => {
    setVoiceMutedPreference(!voiceMuted);
  }, [voiceMuted, setVoiceMutedPreference]);

  const setSpeechRatePreference = useCallback((rate: TutorialSpeechRate) => {
    setSpeechRate(rate);
    writeTutorialSpeechRatePreference(rate);
  }, []);

  const handleDismiss = useCallback(() => {
    cancelTutorialSpeech();
    onComplete();
  }, [onComplete]);

  const startSection = useCallback(
    (sectionId: TutorialSectionId) => {
      cancelTutorialSpeech();
      const nextSteps = buildTutorialStepsForSection(steps, sectionId);
      if (nextSteps.length === 0) return;
      setActiveSteps(nextSteps);
      setStepIndex(0);
      setAutoPlayProgress(0);
      setSpeechComplete(false);
    },
    [steps],
  );

  const goNext = useCallback(() => {
    if (!activeSteps) return;
    cancelTutorialSpeech();
    if (isLast) {
      onComplete();
      return;
    }
    setAutoPlayProgress(0);
    setSpeechComplete(false);
    setStepIndex((current) => Math.min(current + 1, activeSteps.length - 1));
  }, [activeSteps, isLast, onComplete]);

  goNextRef.current = goNext;

  const goBack = useCallback(() => {
    if (!activeSteps) return;
    cancelTutorialSpeech();
    setAutoPlayProgress(0);
    setSpeechComplete(false);
    if (stepIndex === 0) {
      setActiveSteps(null);
      setStepIndex(0);
      return;
    }
    setStepIndex((current) => Math.max(current - 1, 0));
  }, [activeSteps, stepIndex]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleDismiss]);

  useEffect(() => {
    document.body.classList.add("tutorialModal_bodyLock");
    return () => {
      document.body.classList.remove("tutorialModal_bodyLock");
      cancelTutorialSpeech();
    };
  }, []);

  useEffect(() => {
    if (!step) return;
    onStepChange?.(step, showingPicker ? 0 : stepIndex);
  }, [onStepChange, step, stepIndex, showingPicker]);

  useEffect(() => {
    if (showingPicker || !step?.route) return;
    if (routesMatch(step.route, location.pathname, location.search)) return;
    navigate(step.route);
  }, [location.pathname, location.search, navigate, showingPicker, step?.route, stepIndex]);

  // Speak presenter narration when the step is ready.
  useEffect(() => {
    if (showingPicker || !step || !activeSteps) {
      cancelTutorialSpeech();
      setSpeechComplete(false);
      return;
    }
    if (step.kind === "spotlight" && !targetReady) {
      return;
    }

    const narration = getTutorialNarration(step.id);
    setSpeechComplete(false);

    if (!voiceEnabled || !narration) {
      cancelTutorialSpeech();
      setSpeechComplete(true);
      return;
    }

    let cancelled = false;
    speakTutorialNarration(narration, {
      rate: speechRate,
      onEnd: () => {
        if (!cancelled) setSpeechComplete(true);
      },
    });

    return () => {
      cancelled = true;
      cancelTutorialSpeech();
    };
  }, [activeSteps, showingPicker, speechRate, step, stepIndex, targetReady, voiceEnabled]);

  // Auto-play — content dwell when muted; speech end + beat when voice is on.
  useEffect(() => {
    if (!autoPlay || showingPicker || !step || !activeSteps) {
      setAutoPlayProgress(0);
      return;
    }
    if (step.kind === "spotlight" && !targetReady) {
      setAutoPlayProgress(0);
      return;
    }

    const narration = getTutorialNarration(step.id);
    const useSpeechGate = voiceEnabled && Boolean(narration);

    let cancelled = false;
    let raf = 0;

    if (useSpeechGate && !speechComplete) {
      const duration = Math.max(
        1,
        estimateSpeechDurationMs(narration!, speechRate),
      );
      const started = performance.now();
      setAutoPlayProgress(0);

      const tick = (now: number) => {
        if (cancelled) return;
        const progress = Math.min(0.92, (now - started) / duration);
        setAutoPlayProgress(progress);
        raf = window.requestAnimationFrame(tick);
      };

      raf = window.requestAnimationFrame(tick);
      return () => {
        cancelled = true;
        window.cancelAnimationFrame(raf);
      };
    }

    if (useSpeechGate && speechComplete) {
      const started = performance.now();

      const tick = (now: number) => {
        if (cancelled) return;
        const beatProgress = Math.min(
          1,
          (now - started) / TUTORIAL_SPEECH_END_BEAT_MS,
        );
        setAutoPlayProgress(0.92 + beatProgress * 0.08);
        if (beatProgress >= 1) {
          goNextRef.current();
          return;
        }
        raf = window.requestAnimationFrame(tick);
      };

      raf = window.requestAnimationFrame(tick);
      return () => {
        cancelled = true;
        window.cancelAnimationFrame(raf);
      };
    }

    const duration = getTutorialAutoPlayDurationMs(step);
    const started = performance.now();
    setAutoPlayProgress(0);

    const tick = (now: number) => {
      if (cancelled) return;
      const progress = Math.min(1, (now - started) / duration);
      setAutoPlayProgress(progress);
      if (progress >= 1) {
        goNextRef.current();
        return;
      }
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
  }, [
    activeSteps,
    autoPlay,
    showingPicker,
    speechComplete,
    speechRate,
    step,
    stepIndex,
    targetReady,
    voiceEnabled,
  ]);

  const previousOverlayFamilyRef = useRef<"social" | "insight-modal" | null>(null);

  useEffect(() => {
    if (showingPicker || !step || step.kind !== "spotlight") {
      setTargetRect(null);
      setTargetReady(true);
      setTargetMissing(false);
      previousOverlayFamilyRef.current = null;
      return;
    }

    let cancelled = false;
    const family = demoOverlayFamily(step.demoOverlay);
    const stayingInOverlay =
      family != null &&
      family === previousOverlayFamilyRef.current &&
      isDemoOverlayMounted(step.demoOverlay);

    // Keep the prior spotlight/scrim stable while we retarget within the same
    // demo overlay — remounting + loading blur is what made social steps feel glitchy.
    if (!stayingInOverlay) {
      setTargetReady(false);
      setTargetMissing(false);
      setTargetRect(null);
    } else {
      setTargetMissing(false);
    }

    const resolveTarget = async () => {
      if (step.demoOverlay && !stayingInOverlay) {
        await new Promise((resolve) => window.setTimeout(resolve, 160));
      } else if (
        (step.demoOverlay === "social-prompt-request" ||
          step.demoOverlay === "dismiss-insight") &&
        previousOverlayFamilyRef.current === demoOverlayFamily(step.demoOverlay)
      ) {
        // Page / modal swap inside the same overlay family.
        await new Promise((resolve) => window.setTimeout(resolve, 60));
      }

      await expandTourSection(
        step.expandSection,
        step.target?.endsWith("-panel") ? step.target : undefined,
      );

      const element = await waitForTourTarget(step.target);
      if (cancelled) return;

      if (!element) {
        setTargetMissing(true);
        setTargetReady(true);
        previousOverlayFamilyRef.current = family;
        return;
      }

      const refreshed =
        document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`) ??
        element;

      if (!step.skipScroll) {
        scrollTourTargetIntoView(refreshed, step.scrollBlock);
      }

      const measureDelay = stayingInOverlay
        ? 0
        : step.demoOverlay || step.expandSection
          ? 160
          : 280;

      window.setTimeout(() => {
        if (cancelled) return;
        const matches = document.querySelectorAll<HTMLElement>(
          `[data-tour="${step.target}"]`,
        );
        const rect = measureTourTargetElements(matches, {
          maxHeight: step.spotlightClipHeight,
          maxWidth:
            step.spotlightClipWidth != null
              ? Math.min(step.spotlightClipWidth, getTourSidebarWidth())
              : undefined,
          pad: step.spotlightPad,
        });
        if (!rect) {
          setTargetMissing(true);
        }
        setTargetRect(rect);
        setTargetReady(true);
        previousOverlayFamilyRef.current = family;
      }, measureDelay);
    };

    void resolveTarget();

    return () => {
      cancelled = true;
    };
  }, [showingPicker, step, stepIndex, location.pathname]);

  useLayoutEffect(() => {
    if (showingPicker || !step || step.kind !== "spotlight" || !step.target || !targetReady) {
      return;
    }

    const updateRect = () => {
      const matches = document.querySelectorAll<HTMLElement>(
        `[data-tour="${step.target}"]`,
      );
      const rect = measureTourTargetElements(matches, {
        maxHeight: step.spotlightClipHeight,
        maxWidth:
          step.spotlightClipWidth != null
            ? Math.min(step.spotlightClipWidth, getTourSidebarWidth())
            : undefined,
        pad: step.spotlightPad,
      });
      setTargetRect(rect);
      if (!rect) {
        setTargetMissing(true);
      }
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [
    showingPicker,
    step,
    targetReady,
    stepIndex,
    step?.spotlightClipHeight,
    step?.spotlightClipWidth,
    step?.spotlightPad,
    step?.target,
  ]);

  useEffect(() => {
    if (showingPicker || !step?.advanceOnClick || !step.target) return;

    const element = document.querySelector<HTMLElement>(
      `[data-tour="${step.target}"]`,
    );
    if (!element) return;

    const onTargetClick = () => {
      window.setTimeout(() => goNext(), 120);
    };

    element.addEventListener("click", onTargetClick);
    return () => element.removeEventListener("click", onTargetClick);
  }, [goNext, showingPicker, step?.advanceOnClick, step?.target, targetReady]);

  if (!step) return null;

  if (showingPicker) {
    return createPortal(
      <div className="tutorialModal_backdrop" role="presentation" onClick={handleDismiss}>
        <TutorialSectionPicker
          welcomeStep={welcomeStep}
          roleLabel={roleLabel}
          sections={sections}
          roleSteps={steps}
          autoPlay={autoPlay}
          onAutoPlayChange={setAutoPlayPreference}
          voiceMuted={voiceMuted}
          onVoiceMutedChange={setVoiceMutedPreference}
          speechRate={speechRate}
          onSpeechRateChange={setSpeechRatePreference}
          speechSupported={speechSupported}
          onSelect={startSection}
          onClose={handleDismiss}
          onSkip={handleDismiss}
        />
      </div>,
      document.body,
    );
  }

  if (step.kind === "center") {
    return createPortal(
      <div className="tutorialModal_backdrop" role="presentation" onClick={handleDismiss}>
        <CenterStepCard
          step={step}
          roleLabel={roleLabel}
          stepIndex={stepIndex}
          stepCount={tourSteps.length}
          isFirst={isFirst}
          isLast={isLast}
          autoPlay={autoPlay}
          autoPlayProgress={autoPlayProgress}
          onToggleAutoPlay={toggleAutoPlay}
          voiceMuted={voiceMuted}
          onToggleVoiceMute={toggleVoiceMute}
          speechRate={speechRate}
          onSpeechRateChange={setSpeechRatePreference}
          speechSupported={speechSupported}
          onClose={handleDismiss}
          onBack={goBack}
          onNext={goNext}
          onSkip={handleDismiss}
          backLabel={isFirst ? "All topics" : undefined}
        />
      </div>,
      document.body,
    );
  }

  const tooltipStyle = targetRect
    ? computeTooltipStyle(targetRect, step.placement, {
        size: step.tooltipSize ?? "default",
        preferSide: step.tooltipPreferSide,
      })
    : targetReady
      ? fallbackTooltipStyle(step.tooltipSize ?? "default")
      : undefined;

  const scrimLoading = !targetReady;

  return createPortal(
    <div className="tutorialSpotlight_root" role="presentation">
      <TutorialDemoOverlays step={step} />
      <div
        className={`tutorialSpotlight_scrim${scrimLoading ? " tutorialSpotlight_scrim--loading" : ""}`}
        aria-hidden
      />

      {targetRect ? (
        <div
          className="tutorialSpotlight_hole"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
          aria-hidden
        />
      ) : null}

      <AnimatePresence mode="wait">
        <motion.div
          key={step.id}
          className={`tutorialSpotlight_card${
            step.tooltipSize === "compact" ? " tutorialSpotlight_card--compact" : ""
          }`}
          style={
            {
              ...tooltipStyle,
              "--tutorial-accent": step.accent,
              "--tutorial-accent-soft": step.accentSoft,
            } as CSSProperties
          }
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`tutorial-step-${step.id}`}
        >
          <TutorialAutoPlayProgress progress={autoPlayProgress} visible={autoPlay} />
          <div
            className="tutorialSpotlight_cardAccent"
            style={{ background: step.accent }}
            aria-hidden
          />
          <div className="tutorialSpotlight_cardScroll">
            <div className="tutorialSpotlight_cardInner">
              <div className="tutorialSpotlight_cardHeader">
                <span className="tutorialSpotlight_icon" style={{ color: step.accent }} aria-hidden>
                  <i className={`fa-solid ${step.icon}`} />
                </span>
                <div>
                  <p className="tutorialSpotlight_kicker">{step.kicker}</p>
                  <h3 id={`tutorial-step-${step.id}`} className="tutorialSpotlight_title">
                    {step.title}
                  </h3>
                </div>
                <div className="tutorialSpotlight_headerActions">
                  <TutorialSpeechSpeedControl
                    rate={speechRate}
                    onChange={setSpeechRatePreference}
                    disabled={!speechSupported}
                  />
                  <TutorialVoiceMuteToggle
                    muted={voiceMuted || !speechSupported}
                    onToggle={toggleVoiceMute}
                    compact
                    disabled={!speechSupported}
                  />
                  <TutorialAutoPlayToggle
                    enabled={autoPlay}
                    onToggle={toggleAutoPlay}
                    compact
                  />
                  <button
                    type="button"
                    className="tutorialSpotlight_close"
                    onClick={handleDismiss}
                    aria-label="Close guide"
                  >
                    <i className="fa-solid fa-xmark" aria-hidden />
                  </button>
                </div>
              </div>

              <p className="tutorialSpotlight_subtitle">{step.subtitle}</p>
              <p className="tutorialSpotlight_body">{step.body}</p>

              {targetMissing ? (
                <p className="tutorialSpotlight_missing">
                  This area isn&apos;t visible in your current view — press Next to continue.
                </p>
              ) : null}

              {step.advanceOnClick ? (
                <p className="tutorialSpotlight_hint">
                  <i className="fa-solid fa-hand-pointer" aria-hidden /> Click the highlighted area or
                  press Next.
                </p>
              ) : null}

              <ul className="tutorialSpotlight_bullets">
                {step.bullets
                  .slice(0, step.tooltipSize === "compact" ? 2 : 3)
                  .map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
              </ul>

              <div className="tutorialSpotlight_meta">
                Step {stepIndex + 1} of {tourSteps.length}
              </div>
            </div>
          </div>

          <div className="tutorialSpotlight_cardFooter">
            <TutorialFooter
              isFirst={isFirst}
              isLast={isLast}
              onBack={goBack}
              onNext={goNext}
              onSkip={handleDismiss}
              nextLabel={isLast ? "Finish" : "Next"}
              backLabel={isFirst ? "All topics" : undefined}
            />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body,
  );
}

/** @deprecated Use InteractiveTutorial — kept as alias for imports. */
export const TutorialModal = InteractiveTutorial;
